import { c as _c } from "react/compiler-runtime";
import { Button } from "@/components/ui/Button";
import type { ButtonProps } from "@/components/ui/Button";
import { IconButton } from "@/components/ui/IconButton";
import type { IconButtonProps } from "@/components/ui/IconButton";
import { Skeleton } from "@/components/ui/Skeleton";
import { X } from "lucide-react";
import * as React from "react";
import cn from "twsx";

// Linear applied-filter chip: 24px tall, 6px radius, white surface with a 1px
// hairline border and no shadow, split into Field · Operator · Value · Remove
// segments by full-height hairline dividers. 12px text with 7px segment
// padding; Field and Value are primary text, Operator tertiary (Linear's
// measured operator grey). Each control segment takes the hover fill.
// Remove is a 24px square with a 12px ×.
/** Use when showing one applied list filter that the person can edit or remove in place. */
const FilterChip = t0 => {
  const $ = _c(7);
  let props;
  let ref;
  if ($[0] !== t0) {
    ({
      ref,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = props;
    $[2] = ref;
  } else {
    props = $[1];
    ref = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn("inline-flex h-6 min-w-0 max-w-full shrink-0 items-stretch overflow-hidden rounded-md", "divide-x divide-border-popover border border-border-popover bg-bg-surface text-sm");
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== props || $[5] !== ref) {
    t2 = <div ref={ref} role="group" className={t1} {...props} />;
    $[4] = props;
    $[5] = ref;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  return t2;
};
FilterChip.displayName = "FilterChip";
const FilterChipField = t0 => {
  const $ = _c(8);
  let children;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
  } else {
    children = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== children) {
    t1 = <span className="truncate">{children}</span>;
    $[3] = children;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== t1) {
    t2 = <span className="flex min-w-0 max-w-36 shrink-0 items-center px-1.75 text-text-primary" {...props}>{t1}</span>;
    $[5] = props;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
};
FilterChipField.displayName = "FilterChipField";
const FilterChipOperator = props => {
  const $ = _c(2);
  let t0;
  if ($[0] !== props) {
    t0 = <Button variant="segment" size="segment" type="button" {...props} />;
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
FilterChipOperator.displayName = "FilterChipOperator";
const FilterChipValue = t0 => {
  const $ = _c(11);
  let children;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      children,
      pending: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = props;
    $[3] = t1;
  } else {
    children = $[1];
    props = $[2];
    t1 = $[3];
  }
  const pending = t1 === undefined ? false : t1;
  const t2 = pending || undefined;
  let t3;
  if ($[4] !== children || $[5] !== pending) {
    t3 = pending ? <Skeleton size="xs" shape="pill" className="w-16" /> : <span className="min-w-0 max-w-50 truncate">{children}</span>;
    $[4] = children;
    $[5] = pending;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== props || $[8] !== t2 || $[9] !== t3) {
    t4 = <Button variant="segmentValue" size="segment" type="button" aria-busy={t2} {...props}>{t3}</Button>;
    $[7] = props;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
};
FilterChipValue.displayName = "FilterChipValue";
const FilterChipRemove = props => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <X aria-hidden="true" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] !== props) {
    t1 = <IconButton variant="segment" size="segment" {...props}>{t0}</IconButton>;
    $[1] = props;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};
FilterChipRemove.displayName = "FilterChipRemove";
export { FilterChip, FilterChipField, FilterChipOperator, FilterChipRemove, FilterChipValue };